import { parseColorToRGBA } from '@wix/thunderbolt-components/src/core/mappers/BackgroundOverlay/BackgroundOverlay.utils'
import { getSplitRgbValuesString } from '@wix/thunderbolt-commons/src/color'
import { ThemeFontsAndColorsCssDependencies } from './themeFontsAndColors.types'
import { DEFAULT_DESKTOP_SITE_WIDTH, MOBILE_SITE_WIDTH } from '../../utils/constants'
import { CssObject, SelectorObj } from '../../shared.types'
import { MAX_Z_INDEX, PORTALS_Z_INDEX } from '@wix/thunderbolt-commons'
import { FALLBACK_MAXIMUM_VIEWPORT, FALLBACK_MINIMUM_VIEWPORT } from '../styleBased/styleBasedDomApplier'
import { resolveThemeFontsSpx } from '@wix/thunderbolt-becky-root/src/functionLibraryExtensions/masterPageFunctionLibrary'

const getThemeColorsVars = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeColorsVars = deps!.colorData.reduce((result: CssObject, color, index) => {
		const key = `--color_${index}`
		const rgbValues = getSplitRgbValuesString(color)
		result[key] = rgbValues ? rgbValues : ''
		return result
	}, {})

	return themeColorsVars
}

const getThemeFontsVars = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeFonts = deps!.fontData
	const themeFontsSpxResolved = resolveThemeFontsSpx(deps!.fontData)

	const themeFontsVars = deps?.isScalePropsExperimentOpen
		? Object.fromEntries(
				Object.entries(themeFontsSpxResolved).map(([fontKey, fontValue]) => ['--' + fontKey, fontValue])
		  )
		: themeFonts?.reduce((result: CssObject, font, index) => {
				result[`--font_${index}`] = font
				return result
		  }, {})

	return themeFontsVars
}

const getFreemiumBannerHeight = (deps: ThemeFontsAndColorsCssDependencies) => {
	if (deps?.isWixAdsAllowed && deps?.shouldRenderWixAdsOnNavigation) {
		if (deps.editorName === 'Studio' && !deps.shouldShowCoBrandingBanner) {
			return 30
		} else if (deps.isMobileView) {
			return 38
		}

		return 50
	}
	return 0
}

const getGlobalSiteCssVars = (deps: ThemeFontsAndColorsCssDependencies) => {
	const freemiumBannerHeight = getFreemiumBannerHeight(deps)
	const siteWidth = deps?.siteWidth || (deps?.isMobileView ? MOBILE_SITE_WIDTH : DEFAULT_DESKTOP_SITE_WIDTH)
	return {
		'--wix-ads-height': `${freemiumBannerHeight}px`,
		'--wix-ads-top-height': `${freemiumBannerHeight}px`,
		'--site-width': `${siteWidth}px`,
		'--above-all-z-index': `${MAX_Z_INDEX}`,
		'--portals-z-index': `${PORTALS_Z_INDEX}`,
	}
}

const getFontSmoothingStyles = () => {
	return {
		'-webkit-font-smoothing': 'antialiased',
		'-moz-osx-font-smoothing': 'grayscale',
	}
}

const getCssVarsForDefaultBreakpoint = () => {
	return {
		'--minViewportSize': FALLBACK_MINIMUM_VIEWPORT,
		'--maxViewportSize': FALLBACK_MAXIMUM_VIEWPORT,
	}
}

const DEFAULT_RATIO = 100

const getThemeSpxRatio = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeRatio =
		deps?.themeData?.spx?.resolverType === 'scale'
			? `${DEFAULT_RATIO / deps.themeData?.spx?.refWidth} * var(--one-unit)`
			: '1px'

	return {
		'--theme-spx-ratio': `${themeRatio}`,
	}
}

export const getThemeFontsClasses = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeFontsClasses = deps!.fontData.reduce((result: SelectorObj, _font, index) => {
		const fontColor =
			deps!.fontColorMap && deps!.fontColorMap[`font_${index}`] ? deps!.fontColorMap[`font_${index}`] : undefined
		const color = parseColorToRGBA(fontColor, 1)
		const letterSpacingValue = deps!.textTheme ? deps!.textTheme[index]?.letterSpacing : null
		const letterSpacing: { string: string } | {} = letterSpacingValue
			? { 'letter-spacing': letterSpacingValue }
			: {}
		result[`.font_${index}`] = {
			font: `var(--font_${index})`,
			color,
			...letterSpacing,
		}
		return result
	}, {})

	return themeFontsClasses
}

export const getThemeColorsClasses = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeColorsClasses = deps?.colorData.reduce((result: SelectorObj, _, index) => {
		result[`.color_${index}`] = { color: `rgb(var(--color_${index}))` }
		return result
	}, {})

	return themeColorsClasses
}

export const getThemeBackColorsClasses = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeBackColorsClasses = deps?.colorData.reduce((result: SelectorObj, _, index) => {
		result[`.backcolor_${index}`] = { 'background-color': `rgb(var(--color_${index}))` }
		return result
	}, {})

	return themeBackColorsClasses
}

export const getRootCssVars = (deps: ThemeFontsAndColorsCssDependencies) => {
	const themeColorsVars = getThemeColorsVars(deps)
	const themeFontsVars = getThemeFontsVars(deps)
	const globalSiteCssVars = getGlobalSiteCssVars(deps)
	const fontSmoothingStyles = getFontSmoothingStyles()
	const cssVarsForDefaultBreakpoint = getCssVarsForDefaultBreakpoint()
	const themeRatio = getThemeSpxRatio(deps)

	return {
		':root,:host, .spxThemeOverride': {
			...themeColorsVars,
			...themeFontsVars,
			...globalSiteCssVars,
			...fontSmoothingStyles,
			...cssVarsForDefaultBreakpoint,
			...themeRatio,
		},
	}
}
